import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import axiosInstance from "../../utils/api";
import { useNavigate } from "react-router-dom";
import KeywordCluster from "../../pages/Blog/KeywordCluster";
import "./BlogOptimizeModal.css";
import { toast } from "react-toastify";
import { getAgentId } from "../../helpers";

const BlogTypeModal = ({ show, onClose, uid }) => {
  const [blogTypes, setBlogTypes] = useState([]);
  const [selectedBlogType, setSelectedBlogType] = useState(null);
  const [gscTags, setGscTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [bulkKeywordInput, setBulkKeywordInput] = useState("");
  // Bulk keyword paste handler
  const handleBulkKeywordPaste = (e) => {
    const pastedText = e.clipboardData.getData("text");
    // Split by comma, newline, or tab
    const keywords = pastedText
      .split(/,|\n|\t/)
      .map((kw) => kw.trim())
      .filter((kw) => kw.length > 0);
    // Deduplicate and filter out already selected
    const existing = selectedTags.map((tag) => tag.value);
    const newKeywords = Array.from(
      new Set(keywords.filter((kw) => !existing.includes(kw)))
    );
    if (newKeywords.length > 0) {
      setSelectedTags([
        ...selectedTags,
        ...newKeywords.map((kw) => ({ value: kw, label: kw })),
      ]);
    }
    setBulkKeywordInput("");
    e.preventDefault();
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [streamedData, setStreamedData] = useState("");
  const [streamError, setStreamError] = useState(null);
  const [agentId] = useState(
    `blog_agent_${Math.random().toString(36).substring(2)}`
  );
  const [sessionId] = useState(
    `session_${Math.random().toString(36).substring(2)}`
  );
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [showAddNew, setShowAddNew] = useState(false);
  const [newBlogTypeTitle, setNewBlogTypeTitle] = useState("");
  const [newBlogTypeDesc, setNewBlogTypeDesc] = useState("");

  const cleanupJsonString = (str) => {
    return str
      .replace(/\s+/g, " ")
      .replace(/ +([,:])/g, "$1")
      .trim();
  };

  const cleanupKey = (key) => {
    return key
      .replace(/["'\s]+/g, "")
      .replace(/_+/g, "_")
      .trim();
  };

  const cleanupJsonObject = (obj) => {
    if (typeof obj !== "object" || obj === null) {
      return typeof obj === "string" ? cleanupJsonString(obj) : obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => cleanupJsonObject(item));
    }

    return Object.entries(obj).reduce((acc, [key, value]) => {
      // Clean up the key
      const cleanKey = cleanupKey(key);
      // Clean up the value recursively
      acc[cleanKey] = cleanupJsonObject(value);
      return acc;
    }, {});
  };

  useEffect(() => {
    if (!show) return;
    setLoading(true);
    setError(null);
    
    // Load blog types and tags separately so one failure doesn't block the other
    const loadBlogTypes = axiosInstance
      .get("/api/v1/retrive_blog_types")
      .then((res) => {
        const blogTypes = res.data?.blog_types || [];
        setBlogTypes(blogTypes.length > 0 ? blogTypes : []);
      })
      .catch((err) => {
        console.error("Failed to load blog types:", err);
        toast.error("Failed to load blog types");
        setBlogTypes([]);
      });

    const loadGscTags = axiosInstance
      .get("/api/v1/get_gsc_tags")
      .then((res) => {
        const tags = res.data?.tags || [];
        setGscTags(tags.map((tag) => ({ value: tag, label: tag })));
      })
      .catch((err) => {
        console.error("Failed to load GSC tags:", err);
        toast.error("Failed to load tags");
        setGscTags([]);
      });
    Promise.allSettled([loadBlogTypes, loadGscTags]).then(() => {
      setLoading(false);
    });
  }, [show]);

  const handleGenerateKeywordClusters = async () => {
    setStreaming(true);
    setStreamedData("");
    setStreamError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token not found");
        return;
      }

      const blogType = selectedBlogType?.title;
      const seedKeywords = selectedTags.map((tag) => tag.value);

      if (!blogType || !seedKeywords.length) {
        toast.error("Please select both blog type and keywords");
        setStreaming(false);
        return;
      }

      // Get the KeywordClusterSmith agent ID from localStorage
      const keywordClusterAgentId = getAgentId("KeywordClusterSmith");
      if (!keywordClusterAgentId) {
        toast.error("KeywordClusterSmith agent ID not found. Please log in again.");
        setStreaming(false);
        return;
      }

      // Build variables object for the new API format
      const variables = {
        uid: uid || "",
        step: 1,
        blog_type: blogType,
        blog_type_description: selectedBlogType?.description || "",
        seed_keywords: seedKeywords.join(","),
        section_name: selectedBlogType?.title || "",
      };

      // Build query string with keywords
      const query = seedKeywords.join(", ");

      // Build FormData for the new API endpoint
      const formData = new FormData();
      formData.append('agent_id', keywordClusterAgentId.toString());
      formData.append('session_id', sessionId);
      formData.append('streaming', 'true');
      
      // Add query with keywords
      formData.append('query', query);
      
      // Add variables as JSON string
      const variablesString = JSON.stringify(variables);
      formData.append('variables', variablesString);

      const response = await fetch(
        `${axiosInstance.defaults.baseURL}/api/v1/fouray/execute`,
        {
          method: "POST",
          headers: {
            Authorization: token,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        toast.error(
          `Server error: ${response.status} - ${
            errorText || response.statusText
          }`
        );
        setStreaming(false);
        return;
      }

      if (!response.body) {
        toast.error("No response body received");
        setStreaming(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let buffer = "";
      let jsonString = "";
      
      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;

        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";
          
          for (const line of lines) {
            if (line.startsWith("data: ")) {
              // Remove "data: " prefix but preserve all spaces in the content
              const cleaned = line.replace(/^data:\s?/, "");
              
              // Skip empty lines (after removing prefix) and [DONE] marker
              const trimmed = cleaned.trim();
              if (trimmed === "" || trimmed === "[DONE]") {
                continue;
              }
              
              // Skip only the complete run_meta JSON object (it comes as a single complete JSON on one line)
              if (trimmed.startsWith('{') && trimmed.includes('"type"') && trimmed.includes('"run_meta"')) {
                continue;
              }
              
              // Accumulate all other JSON data chunks (preserving spaces)
              jsonString += cleaned;
              setStreamedData(jsonString);
            }
          }
        }
      }

      // Handle any remaining buffer
      if (buffer.trim().startsWith("data: ")) {
        const cleanedData = buffer.replace(/^data:\s*/, "");
        const trimmed = cleanedData.trim();
        if (trimmed && trimmed !== "[DONE]") {
          // Skip if it's the run_meta object
          if (!(trimmed.startsWith('{') && trimmed.includes('"type"') && trimmed.includes('"run_meta"'))) {
            jsonString += cleanedData;
          }
        }
      }

      let parsedData;
      try {
        // First parse the JSON string
        parsedData = JSON.parse(jsonString);
        
        // Transform the response format to match expected structure
        // The API returns { metadata: {...}, clusters: [...], ... }
        // We need to transform it to { semantic_clusters: [...] }
        if (parsedData.clusters && Array.isArray(parsedData.clusters)) {
          // Transform clusters to semantic_clusters format
          const semanticClusters = parsedData.clusters.map((cluster, index) => ({
            id: cluster.cluster_id || `C${index + 1}`,
            cluster_name: cluster.label || cluster.target_h2 || "",
            keywords: cluster.keywords || [],
            intent: cluster.intent || "",
            priority: cluster.priority || "",
            notes: cluster.notes || "",
          }));
          
          
          parsedData = {
            semantic_clusters: semanticClusters,
            blog_type: parsedData.metadata?.blog_type || "",
            metadata: parsedData.metadata || {},
          };
        }
        
        // Then clean up the parsed data
        parsedData = cleanupJsonObject(parsedData);
      } catch (e) {
        console.error("Failed to parse streamed JSON:", e);
        console.error("JSON string length:", jsonString.length);
        console.error("JSON string preview:", jsonString.substring(0, 500));
        toast.error("Failed to parse response data. Please try again.");
        setStreaming(false);
        return;
      }

      if (!parsedData) {
        toast.error("No data received from server");
        setStreaming(false);
        return;
      }

      navigate("/skills/blog/keyword-cluster", {
        state: {
          clusterData: parsedData,
          session_id: sessionId,
          agent_id: keywordClusterAgentId,
          uid: uid,
        },
      });
    } catch (err) {
      toast.error("Generation error:", err);
      setStreamError(err.message || "Failed to generate keyword clusters");
    } finally {
      setStreaming(false);
    }
  };

  function normalizeSections(data) {
    if (!data) return [];
    if (Array.isArray(data.sections)) return data.sections;
    if (data.sections && typeof data.sections === "object")
      return [data.sections];
    // If the API returns h1_title/h2_sections, convert to your expected structure
    if (data.h1_title || data.h2_sections) {
      return [
        {
          title: (data.h1_title || "").trim(),
          points: Array.isArray(data.h2_sections)
            ? data.h2_sections.map((s) => s.trim())
            : [],
        },
      ];
    }
    return [];
  }

  // Helper function to render keyword badges
  const renderKeywordBadges = () => {
    const defaultKeywords = [
      { icon: "🔍", label: "semrush", value: "semrush" },
      { icon: "🔍", label: "search console", value: "search console" },
    ];

    return (
      <div className="keyword-badges">
        {defaultKeywords.map((keyword, index) => (
          <div
            key={index}
            className={`keyword-badge ${
              selectedTags.some((tag) => tag.value === keyword.value)
                ? "selected"
                : ""
            }`}
            onClick={() => {
              const isSelected = selectedTags.some(
                (tag) => tag.value === keyword.value
              );
              if (isSelected) {
                setSelectedTags(
                  selectedTags.filter((tag) => tag.value !== keyword.value)
                );
              } else {
                setSelectedTags([...selectedTags, keyword]);
              }
            }}
          >
            <span className="keyword-label">{keyword.label}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="lg"
      className="blog-type-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Blog type</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-0">
        {loading ? (
          <div className="modal-loading">Loading...</div>
        ) : error ? (
          <div className="modal-error">{error}</div>
        ) : (
          <>
            <div className="keywords-section">
              <div className="section-label">Keywords</div>
              <div
                className="keyword-input-vertical"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  marginBottom: "8px",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-tag input-icon"
                  >
                    <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"></path>
                    <circle
                      cx="7.5"
                      cy="7.5"
                      r=".5"
                      fill="currentColor"
                    ></circle>
                  </svg>
                  <span style={{ fontWeight: 500, fontSize: 15 }}>
                    Paste or type keywords below
                  </span>
                </div>
                <textarea
                  className="form-control keyword-bulk-textarea"
                  style={{ minHeight: 48, resize: "vertical" }}
                  placeholder="Paste or type keywords here (comma, newline, supported)"
                  value={bulkKeywordInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Check if the last character is a newline (enter key pressed)
                    if (value.endsWith('\n')) {
                      // Split by comma, newline, or tab and process keywords
                      const keywords = value
                        .split(/,|\n|\t/)
                        .map((kw) => kw.trim())
                        .filter((kw) => kw.length > 0);
                      
                      if (keywords.length > 0) {
                        // Deduplicate and filter out already selected
                        const existing = selectedTags.map((tag) => tag.value);
                        const newKeywords = Array.from(
                          new Set(keywords.filter((kw) => !existing.includes(kw)))
                        );
                        
                        if (newKeywords.length > 0) {
                          setSelectedTags([
                            ...selectedTags,
                            ...newKeywords.map((kw) => ({ value: kw, label: kw })),
                          ]);
                        }
                      }
                      setBulkKeywordInput("");
                    } else {
                      setBulkKeywordInput(value);
                    }
                  }}
                  onPaste={handleBulkKeywordPaste}
                />
                <CreatableSelect
                  isMulti
                  options={gscTags}
                  value={selectedTags}
                  onChange={setSelectedTags}
                  placeholder="Type to search keywords..."
                  classNamePrefix="keyword-select"
                  className="keyword-select-container"
                  formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                  isValidNewOption={(inputValue, selectValue, selectOptions) =>
                    !!inputValue &&
                    !selectOptions.some((option) => option.value === inputValue)
                  }
                />
              </div>
              {/* <div className="tag-suggestions">
                <span className="tag-pill">competition score</span>
                <span className="tag-pill">optimization tips</span>
              </div> */}
            </div>

            <div className="blog-type-section">
              <div className="section-label">Blog type</div>
              <div className="section-subtitle">
                Select the type of blog you want to generate
              </div>
              <div className="blog-type-options">
                {blogTypes.map((type) => (
                  <div
                    key={type.id || type.title}
                    onClick={() => setSelectedBlogType(type)}
                    className={`blog-type-option ${
                      selectedBlogType === type ? "selected" : ""
                    }`}
                  >
                    <div className="blog-type-icon">
                      <img
                        src="/files.png"
                        alt="blog type"
                        className="blog-type-icon-img"
                      />
                    </div>
                    <div className="blog-type-content">
                      <div className="blog-type-title">{type.title}</div>
                      <div className="blog-type-description">
                        {type.description}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add New Blog Type Form and Button at the bottom */}
                {showAddNew && (
                  <div
                    style={{
                      margin: "16px 0",
                      padding: 12,
                      background: "#f9fafb",
                      borderRadius: 8,
                      border: "1px solid #eee",
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Blog type title"
                      value={newBlogTypeTitle}
                      onChange={(e) => setNewBlogTypeTitle(e.target.value)}
                      style={{
                        width: "100%",
                        padding: 8,
                        borderRadius: 6,
                        border: "1px solid #ccc",
                        marginBottom: 8,
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Description (optional)"
                      value={newBlogTypeDesc}
                      onChange={(e) => setNewBlogTypeDesc(e.target.value)}
                      style={{
                        width: "100%",
                        padding: 8,
                        borderRadius: 6,
                        border: "1px solid #ccc",
                        marginBottom: 8,
                      }}
                    />
                    <button
                      className="btn"
                      style={{
                        background: "#E36250",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 16px",
                        fontWeight: 500,
                        fontSize: 14,
                      }}
                      disabled={!newBlogTypeTitle.trim()}
                      onClick={() => {
                        const newType = {
                          title: newBlogTypeTitle.trim(),
                          description: newBlogTypeDesc.trim(),
                          id: `custom-${Date.now()}`,
                        };
                        setBlogTypes((prev) => [...prev, newType]);
                        setSelectedBlogType(newType);
                        setNewBlogTypeTitle("");
                        setNewBlogTypeDesc("");
                        setShowAddNew(false);
                      }}
                    >
                      Add
                    </button>
                  </div>
                )}
              </div>
              <div class="d-flex justify-content-center">
                <div class="d-flex  align-items-center gap-2">
                  <button
                    type="button"
                    className="btn bgprimary"
                    onClick={() => setShowAddNew((prev) => !prev)}
                  >
                    {showAddNew ? "Cancel" : "Add New Blog Type"}
                  </button>
                  <button
                    className=" next-button"
                    disabled={
                      !selectedBlogType ||
                      selectedTags.length === 0 ||
                      streaming
                    }
                    onClick={() => {
                      if (selectedTags.length === 0) {
                        toast.error("Please add keywords before generating clusters");
                        return;
                      }
                      if (!selectedBlogType) {
                        toast.error("Please select a blog type");
                        return;
                      }
                      handleGenerateKeywordClusters();
                    }}
                    style={{
                      opacity: (!selectedBlogType || selectedTags.length === 0 || streaming) ? 0.5 : 1,
                      cursor: (!selectedBlogType || selectedTags.length === 0 || streaming) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {streaming ? "Generating..." : "Generate keyword clusters"}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default BlogTypeModal;
